import { isIdentityCellLocked, PAN_ROW_FIELD } from '../../../../../common/ManagementProfile/managementProfileFields';
import { validatePAN } from '../../../../../utils/validatePAN';

// Render-only field-config overrides, config-decoupled: (1) fields listed here render
// editable for a not-yet-persisted director row (`isNewDir`), regardless of what the per-org
// `dataToDisplay` config says; (2) a saved valid PAN renders read-only (fill-once lock).
// Never mutates the source config, and never touches the bureau's fetch/consent
// field-requirement set. No bureau-name literal: adding a field or extending to a future
// bureau needs only a change here.
export const ADD_DIRECTOR_REQUIRED_FIELDS = ['pan'] as const;

export const isSavedPanLocked = (savedPan?: string): boolean =>
    isIdentityCellLocked(PAN_ROW_FIELD, { [PAN_ROW_FIELD]: savedPan }) && validatePAN(savedPan ?? '');

export function getEffectiveDirectorFieldConfig(
    dataToDisplay: Record<string, { isEditable: boolean }> | undefined,
    isNewDir: boolean | undefined,
    isPanLocked = false,
): Record<string, { isEditable: boolean }> {
    if (!isNewDir && !isPanLocked) return dataToDisplay ?? {}; // untouched (fetch gate unaffected)
    const effective = { ...(dataToDisplay ?? {}) }; // shallow copy — never mutate the source
    if (isNewDir) {
        for (const field of ADD_DIRECTOR_REQUIRED_FIELDS) {
            effective[field] = { ...(effective[field] ?? {}), isEditable: true };
        }
    }
    if (isPanLocked && effective[PAN_ROW_FIELD]) {
        effective[PAN_ROW_FIELD] = { ...effective[PAN_ROW_FIELD], isEditable: false };
    }
    return effective;
}

// Stamps a freshly persisted PAN on the director's rows in every bureau so the fill-once lock
// follows the director across bureau tabs.
export function applySavedPan<T extends { directorId?: string; pan?: string; savedPan?: string }>(
    prev: Record<string, T[]>,
    directorId?: string,
    pan?: string,
): Record<string, T[]> {
    if (!directorId || !validatePAN(pan ?? '')) return prev;
    const next: Record<string, T[]> = {};
    for (const bureau of Object.keys(prev)) {
        next[bureau] = prev[bureau].map((row) => (row.directorId === directorId ? { ...row, pan, savedPan: pan } : row));
    }
    return next;
}
